import type { FC, ReactNode } from 'react';
import type { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField, FormTagsInput, Label, SimpleSelect } from '@/shared/ui';
import { FieldError } from '@/shared/ui/form';

import type { OfferFormValues } from '../libs/offerSchema';
import { OFFER_TRAFFIC_TYPES } from '../libs/offerSchema';

export interface TagOption {
  icon?: ReactNode;
  label: string;
  value: string;
}

export interface OfferCoverageSectionProps {
  baseOptions: TagOption[];
  control: Control<OfferFormValues>;
  countryOptions: TagOption[];
  currencyOptions: TagOption[];
  exceptionOptions: TagOption[];
  integrationOptions: TagOption[];
  regionOptions: TagOption[];
  showExceptions: boolean;
}

export const OfferCoverageSection: FC<OfferCoverageSectionProps> = ({
  baseOptions,
  control,
  countryOptions,
  currencyOptions,
  exceptionOptions,
  integrationOptions,
  regionOptions,
  showExceptions,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-3">
      <FormField
        control={control}
        name="regions"
        render={({ field }) => (
          <div className="space-y-2">
            <Label>{t('pages.admin.offers.fields.regions')}</Label>
            <FormTagsInput
              restrict
              options={regionOptions}
              placeholder={t('pages.admin.offers.fields.regionsPlaceholder')}
              value={field.value}
              onChange={field.onChange}
            />
          </div>
        )}
      />
      <FormField
        control={control}
        name="countries"
        render={({ field }) => (
          <div className="space-y-2">
            <Label>{t('pages.admin.offers.fields.countries')}</Label>
            <FormTagsInput
              restrict
              options={countryOptions}
              placeholder={t('pages.admin.offers.fields.countriesPlaceholder')}
              value={field.value}
              onChange={field.onChange}
            />
          </div>
        )}
      />
      {showExceptions && (
        <FormField
          control={control}
          name="restricted_countries"
          render={({ field }) => (
            <div className="space-y-2">
              <Label>{t('pages.admin.offers.fields.exceptions')}</Label>
              <FormTagsInput
                restrict
                options={exceptionOptions}
                placeholder={t(
                  'pages.admin.offers.fields.exceptionsPlaceholder',
                )}
                value={field.value}
                onChange={field.onChange}
              />
            </div>
          )}
        />
      )}
      <FormField
        control={control}
        name="currencies"
        render={({ field }) => (
          <div className="space-y-2">
            <Label>{t('pages.admin.offers.fields.currencies')}</Label>
            <FormTagsInput
              restrict
              options={currencyOptions}
              placeholder={t('pages.admin.offers.fields.currenciesPlaceholder')}
              value={field.value}
              onChange={field.onChange}
            />
          </div>
        )}
      />
      {baseOptions.length > 0 && (
        <FormField
          control={control}
          name="base_currency"
          render={({ error, field }) => {
            const single = baseOptions.length === 1;

            return (
              <div className="space-y-1.5">
                <Label className="text-[11px] font-medium text-muted-foreground">
                  {t('pages.admin.offers.fields.baseCurrency')}
                  {!single && <span className="ml-0.5 text-rose-500">*</span>}
                </Label>
                <SimpleSelect
                  aria-label={t('pages.admin.offers.fields.baseCurrency')}
                  disabled={single}
                  options={
                    single
                      ? baseOptions
                      : [
                          {
                            label: t(
                              'pages.admin.offers.fields.baseCurrencyPlaceholder',
                            ),
                            value: '',
                          },
                          ...baseOptions,
                        ]
                  }
                  fullWidth
                  triggerClassName="h-10"
                  value={field.value}
                  onValueChange={field.onChange}
                />
                <p className="text-[11px] text-muted-foreground">
                  {single
                    ? t('pages.admin.offers.fields.baseCurrencyAuto')
                    : t('pages.admin.offers.fields.baseCurrencyHint')}
                </p>
                <FieldError error={error} />
              </div>
            );
          }}
        />
      )}
      <FormField
        control={control}
        name="traffic_type"
        render={({ field }) => (
          <FormTagsInput
            restrict
            label={t('pages.admin.offers.fields.trafficType')}
            options={OFFER_TRAFFIC_TYPES.map((value) => ({
              label: value,
              value,
            }))}
            placeholder={t('pages.admin.offers.fields.trafficPlaceholder')}
            value={field.value
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean)}
            onChange={(next) => field.onChange(next.join(', '))}
          />
        )}
      />
      <FormField
        control={control}
        name="integration_methods"
        render={({ field }) => (
          <div className="space-y-2">
            <Label>{t('pages.admin.offers.fields.integrationType')}</Label>
            <FormTagsInput
              restrict
              options={integrationOptions}
              placeholder={t(
                'pages.admin.offers.fields.integrationPlaceholder',
              )}
              value={field.value}
              onChange={field.onChange}
            />
          </div>
        )}
      />
    </div>
  );
};

OfferCoverageSection.displayName = 'OfferCoverageSection';
